import {i18n} from '@online-offer/i18n';
import {Alert} from 'platform/components';
import {Center, Show, Spinner, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNil} from 'ramda';

import {useLatest, useOnMount} from 'shared/web';

import {Section} from '../../components/Section/Section';
import {FinancialProductCard} from './FinancialProductCard';
import {FinancialProductControls} from './FinancialProductControls';
import {subscribeToOpenFinancialProduct} from './financialProductNavigation';
import {FinancialProductNotices} from './FinancialProductNotices';
import {FinancialProductOfferDetail} from './FinancialProductOfferDetail';
import {FinancialProductProviderFacts} from './FinancialProductProviderFacts';
import {FinancialProductResultPanel} from './FinancialProductResultPanel';
import {FinancialProductSignedSnapshotNotice} from './FinancialProductSignedSnapshotNotice';
import {FinancialProductTabs} from './FinancialProductTabs';
import {hasChoosableConfiguration} from './preference/hasChoosableConfiguration';
import {useFinancialProductPreference} from './useFinancialProductPreference';

/**
 * How the vehicle can be paid for, as the customer holding the offer link sees
 * and shapes it.
 *
 * Releasing a control stores that choice, and the stored answer is its quote —
 * the customer never confirms a quote the frontend assembled, and the seller
 * sees the same choice on the business case. While a slider is still being
 * dragged, the last quote stays on screen. A product nobody
 * configured is absent from the payload and gets no tab; one the vehicle no
 * longer offers anything on is present but unavailable, and keeps a tab only
 * while it is the customer's stored choice or the tab they are on. One that is
 * unavailable only on the stored choice keeps its tab and opens on a choice that
 * can be bought. A vehicle with nothing configured at all renders no section.
 *
 * Nothing here decides for itself what an offer now says: a refused write and a
 * signed contract both answer with the authoritative state, and the surface
 * renders that rather than the choice it was refused.
 */
export function FinancialProducts() {
  const {
    products,
    offers,
    refusal,
    rebased,
    isReadOnly,
    opened,
    quoteLifecycle,
    commit,
    openProduct,
  } = useFinancialProductPreference();

  const latestOpenProduct = useLatest(openProduct);
  const latestOffers = useLatest(offers);
  const unsubscribe = useRef<VoidFunction | null>(null);

  useOnMount(
    () => {
      unsubscribe.current = subscribeToOpenFinancialProduct((productType) => {
        if (latestOffers.current.some((offer) => offer.productType === productType)) {
          latestOpenProduct.current(productType);
        }
      });
    },
    () => unsubscribe.current?.()
  );

  if (isNil(opened)) {
    return null;
  }

  return (
    <Section id="FINANCIAL_PRODUCTS" data-testid="financialProducts">
      <VStack spacing={6}>
        <Show when={offers.length > 1}>
          <FinancialProductTabs
            offers={offers}
            value={opened.offer.productType}
            onChange={openProduct}
            data-testid="financialProducts-product"
          />
        </Show>
        <FinancialProductNotices
          offer={opened.offer}
          refusal={refusal}
          rebased={rebased}
          data-testid="financialProducts"
        />
        <FinancialProductCard
          controls={match(!isReadOnly && hasChoosableConfiguration(opened.offer))
            .with(true, () => (
              <FinancialProductControls
                offer={opened.offer}
                coordinates={opened.coordinates}
                vehiclePriceWithVat={products?.vehiclePriceWithVat}
                onChange={commit}
                data-testid="financialProducts"
              />
            ))
            .otherwise(() => null)}
          result={
            <FinancialProductResultPanel>
              {match(quoteLifecycle)
                .with('pending', () => (
                  <Center height="100%" grow={1}>
                    <Spinner
                      size="large"
                      color="light"
                      data-testid="financialProducts-quotePending"
                    />
                  </Center>
                ))
                .with('failed', () => (
                  <Alert
                    type="inline"
                    variant="warning"
                    message={i18n.t('financialProducts.unavailable.generic')}
                    data-testid="financialProducts-quoteFailed"
                  />
                ))
                .with('ready', () => (
                  <FinancialProductOfferDetail
                    offer={opened.offer}
                    data-testid="financialProducts"
                  />
                ))
                .exhaustive()}
            </FinancialProductResultPanel>
          }
          footer={
            <Show when={quoteLifecycle === 'ready'}>
              <FinancialProductProviderFacts offer={opened.offer} />
            </Show>
          }
        />
        <Show when={products?.mode === 'CONTRACT_SNAPSHOT'}>
          <FinancialProductSignedSnapshotNotice data-testid="financialProducts" />
        </Show>
      </VStack>
    </Section>
  );
}
